import React, {useEffect, useMemo, useRef, type FC} from 'react';
import s from './Thumbnail.scss';
import {Play} from '../../../../../icons/dist';
import type {IMediaItem} from '../../../../../types/app-types';
import {type ProvidedGlobalProps, withGlobalProps} from '../../../../../providers/globalPropsProvider';
import {useProductGallery} from '../../../ProductGalleryProvider/ProductGalleryProvider';
import {useSwiper, useSwiperSlide} from 'swiper/react';
import {ImageLoadingBehaviorOptions, ThumbnailImage} from '@wix/wix-ui-tpa/cssVars';
import {classes as stylableClasses, st} from './Thumbnail.st.css';
import {useHasVisibleArrows} from '../utils/hooks/useHasVisibleArrows';
import {
  type IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {SPECS} from '../../../../../specs';

export enum ThumbnailDataHooks {
  Root = 'thumbnail-root',
  Image = 'thumbnail-image',
  VideoOverlay = 'video-overlay',
}

export interface ThumbnailComponentProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  mediaItem: IMediaItem;
  index: number;
  thumbnailsWidth: number;
  thumbnailsHeight: number;
}

const ThumbnailComponent: FC<ThumbnailComponentProps> = ({
  globals: {product, isSEO, useExperiments},
  thumbnailsWidth,
  thumbnailsHeight,
  t,
  mediaItem,
  index,
}) => {
  const swiper = useSwiper();
  const {isActive} = useSwiperSlide();
  const hasVisibleArrows = useHasVisibleArrows(swiper);
  const {selectedIndex, hasSelectedIndexChanged} = useProductGallery();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isNewThumbnailDesignEnabled = useExperiments.enabled(SPECS.ImageSettingsInViewer);

  useEffect(() => {
    /* istanbul ignore next */
    if (!buttonRef.current) {
      return;
    }
    if (selectedIndex === index && hasSelectedIndexChanged) {
      buttonRef.current.focus();
    }
  }, [selectedIndex, index, hasSelectedIndexChanged]);

  const tabIndex = useMemo(() => {
    if (!hasVisibleArrows) {
      return 0;
    }
    return isActive ? 0 : -1;
  }, [isActive, hasVisibleArrows]);

  const isSelected = selectedIndex === index;

  return (
    <button
      type="button"
      className={s.root}
      data-hook={ThumbnailDataHooks.Root}
      tabIndex={tabIndex}
      ref={buttonRef}
      aria-pressed={isSelected}>
      {mediaItem.mediaType === 'VIDEO' && <VideoOverlay />}
      <ThumbnailImage
        data-hook={ThumbnailDataHooks.Image}
        className={st(stylableClasses.thumbnailImage, {isSelected, isNewThumbnailDesignEnabled})}
        sourceHeight={thumbnailsHeight}
        sourceWidth={thumbnailsWidth}
        width={thumbnailsWidth}
        height={thumbnailsHeight}
        src={mediaItem.url}
        alt={t('productPage.gallery.thumbnail.ariaLabel', {productName: mediaItem.altText || product.name})}
        isSEOBot={isSEO}
        shouldUseLQIP
        encoding="AVIF"
        loadingBehavior={ImageLoadingBehaviorOptions.none}
      />
    </button>
  );
};

const VideoOverlay = () => (
  <div className={s.videoOverlay} data-hook={ThumbnailDataHooks.VideoOverlay}>
    <Play />
  </div>
);

export const Thumbnail = withGlobalProps(withTranslations('globals.texts')(ThumbnailComponent));
